import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import { tienePermiso } from '../utils/navPermisos';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import LoadErrorCard from '../components/LoadErrorCard';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit, Trash2, Loader2, FolderTree, 
  AlertCircle, Download, Upload, Building, CheckCircle, XCircle, FileWarning,
  ChevronRight, Filter, ChevronDown
} from 'lucide-react';

const ProyectosPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const themeColors = useThemeColors();
  // Acceso gobernado por la matriz de permisos (SUPERADMIN siempre pasa).
  const esSuper = user?.rol === 'SUPERADMIN';
  const puedeCrear = esSuper || tienePermiso(user, 'proyectos', 'crear');
  const puedeEditar = esSuper || tienePermiso(user, 'proyectos', 'editar');
  const puedeEliminar = esSuper || tienePermiso(user, 'proyectos', 'eliminar');
  const canEdit = puedeEditar || puedeEliminar;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [search, setSearch] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProyecto, setEditingProyecto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Export/Import states
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nombre: '',
    razon_social: '',
    ruc: '',
    direccion: '',
    distrito: '',
    codigo: '',
    codigo_proyecto: '',
    correlativo: 1,
    reportar_sirec: false,
  });

  const fetchProyectos = async (signal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.get('/proyectos', { signal });
      setProyectos(response.data);
    } catch (err) {
      if (isCanceled(err)) return;
      console.error('Error fetching proyectos:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchProyectos(signal), []);

  const openCreate = () => {
    setEditingProyecto(null);
    setFormData({
      nombre: '',
      razon_social: '',
      ruc: '',
      direccion: '',
      distrito: '',
      codigo: '',
      codigo_proyecto: '',
      correlativo: 1,
      reportar_sirec: false,
    });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (proyecto) => {
    setEditingProyecto(proyecto);
    setFormData({
      nombre: proyecto.nombre,
      razon_social: proyecto.razon_social,
      ruc: proyecto.ruc,
      direccion: proyecto.direccion || '',
      distrito: proyecto.distrito || '',
      codigo: proyecto.codigo,
      codigo_proyecto: proyecto.codigo_proyecto || '',
      correlativo: proyecto.correlativo,
      reportar_sirec: proyecto.reportar_sirec || false,
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingProyecto) {
        await api.put(`/proyectos/${editingProyecto.id}`, formData);
      } else {
        await api.post('/proyectos', formData);
      }
      setDialogOpen(false);
      fetchProyectos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el proyecto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (proyecto) => {
    if (!window.confirm(`¿Está seguro de eliminar el proyecto "${proyecto.nombre}"?`)) return;

    try {
      await api.delete(`/proyectos/${proyecto.id}`);
      fetchProyectos();
      toast.success('Proyecto eliminado correctamente');
    } catch (err) {
      console.error('Error deleting proyecto:', err);
      toast.error('Error al eliminar el proyecto');
    }
  };

  // Exportar proyectos a Excel
  const handleExportar = async () => {
    setExporting(true);
    try {
      const response = await api.get('/proyectos/exportar/excel', {
        responseType: 'blob'
      });
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proyectos_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo Excel exportado correctamente');
    } catch (err) {
      console.error('Error exportando:', err);
      toast.error('Error al exportar proyectos');
    } finally {
      setExporting(false);
    }
  };

  // Trigger file input para importar
  const handleImportarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Procesar archivo importado
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar extensión
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
      event.target.value = '';
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/proyectos/importar/excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      setImportDialogOpen(true);
      fetchProyectos(); // Refrescar lista
      
      if (response.data.errores?.length === 0) {
        toast.success(`Importación exitosa: ${response.data.creados} creados, ${response.data.actualizados} actualizados`);
      } else {
        toast.warning('Importación completada con algunos errores');
      }
    } catch (err) {
      console.error('Error importando:', err);
      toast.error(err.response?.data?.detail || 'Error al importar el archivo');
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset input
    }
  };

  const filteredProyectos = proyectos.filter(p => 
    p.nombre.toLowerCase().includes(search.toLowerCase()) ||
    p.razon_social.toLowerCase().includes(search.toLowerCase()) ||
    p.ruc.includes(search) ||
    p.codigo.toLowerCase().includes(search.toLowerCase()) ||
    (p.codigo_proyecto || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="proyectos-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Proyectos</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestión de proyectos y razones sociales
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Input oculto para importar archivos */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            className="hidden"
            data-testid="import-file-input"
          />
          <Button 
            variant="outline" 
            size="sm"
            className="flex-1 sm:flex-none"
            data-testid="importar-btn"
            onClick={handleImportarClick}
            disabled={importing}
          >
            {importing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            <span className="hidden xs:inline">{importing ? 'Importando...' : 'Importar'}</span>
            <span className="xs:hidden">{importing ? '...' : 'Imp.'}</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="flex-1 sm:flex-none"
            data-testid="exportar-btn"
            onClick={handleExportar}
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            <span className="hidden xs:inline">{exporting ? 'Exportando...' : 'Exportar'}</span>
            <span className="xs:hidden">{exporting ? '...' : 'Exp.'}</span>
          </Button>
          {puedeCrear && (
            <Button 
              onClick={openCreate} 
              size="sm"
              className="w-full sm:w-auto text-white"
              data-testid="crear-proyecto-btn" 
              {...getPrimaryButtonStyles(themeColors)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proyecto
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none sm:ml-auto"
            data-testid="subdivisiones-btn"
            onClick={() => navigate('/admin/proyectos/subdivisiones')}
          >
            <FolderTree className="w-4 h-4 mr-2" />
            Subdivisiones
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, razón social, RUC o código..."
              data-testid="busqueda-input"
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Data View - Responsive */}
      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          </CardContent>
        </Card>
      ) : loadError ? (
        <LoadErrorCard onRetry={fetchProyectos} testId="proyectos-load-error" />
      ) : filteredProyectos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <FolderTree className="w-12 h-12 mb-4 text-slate-300" />
              <p>No se encontraron proyectos</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Vista móvil - Cards */}
          <div className="md:hidden space-y-3">
            {filteredProyectos.map((proyecto) => (
              <Card 
                key={proyecto.id}
                className="border-slate-200"
                data-testid={`proyecto-card-${proyecto.id}`}
              >
                <CardContent className="p-4">
                  {/* Header: Código + Estado */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono font-bold text-slate-900">{proyecto.codigo}</p>
                      {proyecto.codigo_proyecto && (
                        <p className="font-mono text-xs text-slate-500" data-testid={`proyecto-codigo-inmobiliario-${proyecto.id}`}>
                          Inmob.: {proyecto.codigo_proyecto}
                        </p>
                      )}
                      <p className="text-sm text-slate-800 font-medium truncate">{proyecto.nombre}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <Badge className={proyecto.activo !== false ? 'status-terminado' : 'bg-slate-100 text-slate-600'}>
                        {proyecto.activo !== false ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Datos principales */}
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-slate-500 block text-xs">Razón Social:</span>
                      <span className="text-slate-800 break-words">{proyecto.razon_social}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-slate-500 block text-xs">RUC:</span>
                        <span className="text-slate-800 font-mono text-xs">{proyecto.ruc}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block text-xs">Correlativo:</span>
                        <span className="text-slate-800 font-mono">{proyecto.correlativo}</span>
                      </div>
                    </div>
                    {proyecto.distrito && (
                      <div>
                        <span className="text-slate-500 block text-xs">Distrito:</span>
                        <span className="text-slate-800">{proyecto.distrito}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-slate-500 text-xs">SIREC:</span>
                      {proyecto.reportar_sirec ? (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-xs">
                          <FileWarning className="w-3 h-3 mr-1" />
                          Sí
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">No</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {canEdit && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-2">
                      {puedeEditar && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEdit(proyecto)}
                          data-testid={`editar-proyecto-mobile-${proyecto.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      )}
                      {puedeEliminar && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(proyecto)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Vista desktop - Tabla */}
          <Card className="border-slate-200 hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Código LDRV</TableHead>
                      <TableHead>Nombre Proyecto</TableHead>
                      <TableHead>Código Inmob.</TableHead>
                      <TableHead className="hidden lg:table-cell">Razón Social</TableHead>
                      <TableHead>RUC</TableHead>
                      <TableHead className="hidden xl:table-cell">Distrito</TableHead>
                      <TableHead>Correlativo</TableHead>
                      <TableHead>SIREC</TableHead>
                      <TableHead>Estado</TableHead>
                      {canEdit && <TableHead></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProyectos.map((proyecto) => (
                      <TableRow key={proyecto.id} className="table-row" data-testid={`proyecto-row-${proyecto.id}`}>
                        <TableCell className="font-mono font-medium text-sm">
                          {proyecto.codigo}
                        </TableCell>
                        <TableCell className="font-medium">{proyecto.nombre}</TableCell>
                        <TableCell className="font-mono text-sm text-slate-600" data-testid={`proyecto-row-codigo-inmobiliario-${proyecto.id}`}>
                          {proyecto.codigo_proyecto || '-'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell max-w-[200px] truncate">
                          {proyecto.razon_social}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{proyecto.ruc}</TableCell>
                        <TableCell className="hidden xl:table-cell">{proyecto.distrito || '-'}</TableCell>
                        <TableCell className="font-mono">{proyecto.correlativo}</TableCell>
                        <TableCell>
                          {proyecto.reportar_sirec ? (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                              <FileWarning className="w-3 h-3 mr-1" />
                              Sí
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={proyecto.activo !== false ? 'status-terminado' : 'bg-slate-100 text-slate-600'}>
                            {proyecto.activo !== false ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1">
                              {puedeEditar && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEdit(proyecto)}
                                  data-testid={`editar-proyecto-${proyecto.id}`}
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {puedeEliminar && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleDelete(proyecto)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              {editingProyecto ? 'Editar Proyecto' : 'Nuevo Proyecto'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">Código LDRV *</Label>
                <Input
                  id="codigo"
                  data-testid="codigo-input"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  maxLength={30}
                  placeholder="CODPRY"
                  required
                  disabled={false}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="correlativo">Correlativo Inicial</Label>
                <Input
                  id="correlativo"
                  type="number"
                  min="1"
                  data-testid="correlativo-input"
                  value={formData.correlativo}
                  onChange={(e) => setFormData({ ...formData, correlativo: parseInt(e.target.value) })}
                  disabled={false}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre del Proyecto *</Label>
                <Input
                  id="nombre"
                  data-testid="nombre-input"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Residencial Los Jardines"
                  required
                  disabled={false}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo_proyecto">Código Inmobiliario</Label>
                <Input
                  id="codigo_proyecto"
                  data-testid="codigo-inmobiliario-input"
                  value={formData.codigo_proyecto}
                  onChange={(e) => setFormData({ ...formData, codigo_proyecto: e.target.value.toUpperCase() })}
                  maxLength={20}
                  placeholder="Código universal del proyecto"
                  disabled={false}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="razon_social">Razón Social *</Label>
              <Input
                id="razon_social"
                data-testid="razon-social-input"
                value={formData.razon_social}
                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                placeholder="Jardines Inmobiliaria S.A.C."
                required
                disabled={false}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ruc">RUC *</Label>
                <Input
                  id="ruc"
                  data-testid="ruc-input"
                  value={formData.ruc}
                  onChange={(e) => setFormData({ ...formData, ruc: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                  placeholder="20123456789"
                  maxLength={11}
                  required
                  disabled={false}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="distrito">Distrito</Label>
                <Input
                  id="distrito"
                  data-testid="distrito-input"
                  value={formData.distrito}
                  onChange={(e) => setFormData({ ...formData, distrito: e.target.value })}
                  placeholder="San Isidro"
                  disabled={false}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                data-testid="direccion-input"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Av. Ejemplo 123"
                disabled={false}
              />
            </div>

            {/* Switch SIREC */}
            <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="space-y-0.5">
                <Label htmlFor="reportar_sirec" className="text-base font-medium text-amber-900 flex items-center gap-2">
                  <FileWarning className="w-4 h-4" />
                  Proyecto debe reportar al SIREC
                </Label>
                <p className="text-sm text-amber-700">
                  Los reclamos de este proyecto generarán notificaciones de alerta
                </p>
              </div>
              <Switch
                id="reportar_sirec"
                checked={formData.reportar_sirec}
                onCheckedChange={(checked) => setFormData({ ...formData, reportar_sirec: checked })}
                data-testid="reportar-sirec-switch"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="guardar-proyecto-btn"
                className="text-white"
                {...getPrimaryButtonStyles(themeColors)}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingProyecto ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Resultado de Importación
            </DialogTitle>
          </DialogHeader>

          {importResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-green-700">{importResult.creados}</p>
                    <p className="text-sm text-green-600">Proyectos Creados</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <Edit className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold text-blue-700">{importResult.actualizados}</p>
                    <p className="text-sm text-blue-600">Proyectos Actualizados</p>
                  </CardContent>
                </Card>
              </div>

              {importResult.errores?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="w-4 h-4" />
                    <span className="font-medium">Errores encontrados ({importResult.errores.length})</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto bg-red-50 border border-red-200 rounded-md p-3">
                    <ul className="text-sm text-red-700 space-y-1">
                      {importResult.errores.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-red-400">•</span>
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              onClick={() => setImportDialogOpen(false)}
              className="text-white"
              {...getPrimaryButtonStyles(themeColors)}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProyectosPage;
